import { updateObject } from "utils";
import { cloneDeep } from "lodash";
import {
    mergeCommunityValidationEcmResults,
    setAllCommunityEcmMeasuresActive,
} from "utils/communities/communityValidation";
import types from "./types";
import { resolveEcmSectionLabelFromUpgradePayload } from "utils/packageUpgradeEcmCategories";

export const initialState = {
    communityId: null,
    community: {
        communityDetails: {},
        modelDir: {},
        modelData: {},
        ecmInputMap: {},
        configuration: {},
    },
    communityToCompare: {
        communityDetails: {},
        modelDir: {},
        modelData: {},
        ecmInputMap: {},
        configuration: {},
    },
    isSavingEcmInputMap: null,
    loading: false,
    error: null,
};

const communityReducer = (state = initialState, action) => {
    switch (action.type) {
        case types.CLEAN_UP_COMMUNITY:
            return { ...initialState };

        case types.CREATE_COMMUNITY_START:
        case types.FETCH_COMMUNITY_START:
            return { ...state, loading: true, error: null };

        case types.CREATE_COMMUNITY_SUCCESS:
            return { ...state, loading: false };
        case types.FETCH_COMMUNITY_SUCCESS:
            return communityInit(state, action);

        case types.CREATE_COMMUNITY_ERROR:
            return { ...state, loading: false, error: action.payload };
        case types.FETCH_COMMUNITY_ERROR:
            return {
                ...state,
                community: null,
                loading: false,
                error: action.payload,
            };

        case types.COMMUNITY_LOADING:
            return startCommunityLoading(state, action);
        case types.COMMUNITY_LOADED:
            return endCommunityLoading(state, action);
        case types.SET_COMMUNITY_ERROR:
            return setCommunityError(state, action);

        case types.ADD_MODEL_TO_COMMUNITY:
            return addCommunityModel(state, action);
        case types.REMOVE_MODEL_FROM_COMMUNITY:
            return removeCommunityModel(state, action);
        case types.UPDATE_COMMUNITY_DETAILS:
            return updateCommunityDetails(state, action);
        case types.UPDATE_COMMUNITY_SETTINGS:
            return updateCommunityUISettings(state, action);
        case types.UPDATE_MODEL_DIR_COPIES:
            return updateModelDuplicates(state, action);
        case types.SAVE_COMMUNTIY_DATA:
            return setInitData(state, action);

        case types.SAVE_COMM_ECM_INPUT_MAP_START:
            return {
                ...state,
                isSavingEcmInputMap: true,
                error: null,
            };
        case types.SAVE_COMM_ECM_INPUT_MAP_SUCCESS:
            // console.log("action.payload.ecmInputMap", action.payload.ecmInputMap);
            return {
                ...state,
                communityToCompare: {
                    ...cloneDeep(state.community),
                    ecmInputMap: action.payload.ecmInputMap,
                },
                isSavingEcmInputMap: false,
                community: {
                    ...state.community,
                    ecmInputMap: action.payload.ecmInputMap,
                },
            };
        case types.SAVE_COMM_ECM_INPUT_MAP_ERROR:
            return {
                ...state,
                isSavingEcmInputMap: false,
                error: action.payload,
            };
        case types.SAVE_COMMUNITY_START:
            return {
                ...state,
                isSavingCommunity: true,
                error: null,
            };
        case types.SAVE_COMMUNITY_SUCCESS:
            return {
                ...state,
                isSavingCommunity: false,
            };
        case types.ADD_UPGRADE_TO_COMMUNITY: {
            // console.log("[addUpgradeToCommunity] action.payload", action.payload);
            const upgradesWithActive = Object.fromEntries(
                Object.entries(action.payload.upgrade || {}).map(([key, val]) => [key, { ...val, active: true }])
            );
            return {
                ...state,
                community: {
                    ...state.community,
                    ecmInputMap: {
                        ...state.community.ecmInputMap,
                        [action.payload.sectionType]: {
                            ...state.community.ecmInputMap[action.payload.sectionType],
                            ...upgradesWithActive,
                        },
                    },
                },
            };
        }
        case types.REMOVE_UPGRADE_FROM_COMMUNITY:
            delete state.community.ecmInputMap[action.payload.sectionType][action.payload.upgradeId];

            // Otherwise, the object reference doesn't change, and the component doesn't re-render
            const ecmInputMap = { ...state.community.ecmInputMap };

            return {
                ...state,
                community: {
                    ...state.community,
                    ecmInputMap,
                },
            };
        case types.UPDATE_COMM_ECM_INPUT_MAP_UPGRADE:
            return {
                ...state,
                community: {
                    ...state.community,
                    ecmInputMap: {
                        ...state.community.ecmInputMap,
                        [action.payload.sectionType]: {
                            ...state.community.ecmInputMap[action.payload.sectionType],
                            [action.payload.upgradeId]: {
                                ...(state.community.ecmInputMap[action.payload.sectionType]?.[
                                    action.payload.upgradeId
                                ] || {}),
                                active: true,
                                passed: null,
                                individualSavings: null,
                                label: resolveEcmSectionLabelFromUpgradePayload(
                                    action.payload.sectionType,
                                    action.payload.upgrade
                                ),
                                packageValues: {
                                    ...(state.community.ecmInputMap[action.payload.sectionType]?.[
                                        action.payload.upgradeId
                                    ]?.packageValues || {}),
                                    ...action.payload.upgrade,
                                },
                            },
                        },
                    },
                },
            };
        case types.UPDATE_COMM_DETAILS:
            return {
                ...state,
                community: {
                    ...state.community,
                    communityDetails: cloneDeep(action.payload.communityDetails),
                },
                communityToCompare: {
                    ...state.communityToCompare,
                    communityDetails: cloneDeep(action.payload.communityDetails),
                },
            };
        case types.MERGE_COMMUNITY_ECM_VALIDATION_RESULTS: {
            const { remoteEcmInputMap } = action.payload;
            return {
                ...state,
                community: {
                    ...state.community,
                    ecmInputMap: setAllCommunityEcmMeasuresActive(
                        mergeCommunityValidationEcmResults(state.community.ecmInputMap, remoteEcmInputMap)
                    ),
                },
                communityToCompare: {
                    ...state.communityToCompare,
                    ecmInputMap: setAllCommunityEcmMeasuresActive(
                        mergeCommunityValidationEcmResults(
                            state.communityToCompare.ecmInputMap || {},
                            remoteEcmInputMap
                        )
                    ),
                },
            };
        }
        case types.UPDATE_COMM_FUEL_COSTS: {
            const { fuelType, name, value } = action.payload;

            const configuration = state.community?.configuration || {};
            const fuelCosts = state.community?.configuration?.fuelCosts || {};
            const fuelCostsType = fuelCosts[fuelType] || {};

            return {
                ...state,
                community: {
                    ...state.community,
                    configuration: {
                        ...configuration,
                        fuelCosts: {
                            ...fuelCosts,
                            [fuelType]: {
                                ...fuelCostsType,
                                [name]: value,
                            },
                        },
                    },
                },
            };
        }
        case types.UPDATE_RUN_RESULTS:
            return {
                ...state,
                community: {
                    ...state.community,
                    parametricResults: action.payload,
                },

                communityToCompare: {
                    ...state.communityToCompare,
                    parametricResults: action.payload,
                },
            };
        default:
            return state;
    }
};

const startCommunityLoading = (state, action) => updateObject(state, { loading: true });

const endCommunityLoading = (state, action) => updateObject(state, { loading: false });

const setCommunityError = (state, action) => updateObject(state, { loading: false, error: action.error });

const communityInit = (state, action) => {
    const ecmNormalized = setAllCommunityEcmMeasuresActive(action.payload.ecmInputMap || {});
    return updateObject(state, {
        communityId: action.payload.communityId,
        community: {
            communityDetails: action.payload.communityDetails,
            modelDir: action.payload.modelDir,
            modelData: action.payload.modelData,
            ecmInputMap: ecmNormalized,
            parametricResults: action.payload.parametricResults,
            configuration: action.payload.configuration,
        },
        communityToCompare: {
            communityDetails: cloneDeep(action.payload.communityDetails),
            modelDir: cloneDeep(action.payload.modelDir),
            modelData: cloneDeep(action.payload.modelData),
            ecmInputMap: cloneDeep(ecmNormalized),
            parametricResults: cloneDeep(action.payload.parametricResults),
            configuration: cloneDeep(action.payload.configuration),
        },
        error: false,
        loading: false,
    });
};

const addCommunityModel = (state, action) => {
    var updatedTotalModels = parseInt(state.community.communityDetails.totalModels) + 1;
    var updatedRegions = [
        ...(state.community.communityDetails.regions || []),
        action.payload.modelDetails.location.regionCode,
    ];

    return updateObject(state, {
        ...state,
        community: {
            ...state.community,
            communityDetails: {
                ...state.community.communityDetails,
                regions: updatedRegions,
                totalModels: updatedTotalModels,
            },
            modelDir: {
                ...state.community.modelDir,
                models: {
                    ...state.community.modelDir.models,
                    [action.payload.modelId]: action.payload.modelDetails,
                },
            },
            modelData: {
                ...state.community.modelData,
                models: {
                    ...state.community.modelData.models,
                    [action.payload.modelId]: action.payload.modelData,
                },
            },
        },
    });
};

const removeCommunityModel = (state, action) => {
    var newModelList = Object.entries(state.community.modelDir.models).filter(
        ([key, value]) => key !== action.payload.modelId
    );

    var newModelData = Object.entries(state.community.modelData.models).filter(
        ([key, value]) => key !== action.payload.modelId
    );

    var updatedTotalModels = parseInt(state.community.communityDetails.totalModels) - 1;

    var updatedRegions = cloneDeep(state.community.communityDetails.regions) || [];
    var idx = updatedRegions.findIndex((p) => p === action.payload.regionCode);
    if (idx >= 0) {
        updatedRegions.splice(idx, 1);
    }

    return updateObject(state, {
        ...state,
        community: {
            ...state.community,
            communityDetails: {
                ...state.community.communityDetails,
                regions: updatedRegions,
                totalModels: updatedTotalModels,
            },
            modelDir: {
                ...state.community.modelDir,
                models: Object.fromEntries(newModelList),
            },
            modelData: {
                ...state.community.modelData,
                models: Object.fromEntries(newModelData),
            },
        },
    });
};

const updateCommunityDetails = (state, action) => {
    return updateObject(state, {
        ...state,
        community: {
            ...state.community,
            communityDetails: {
                ...state.community.communityDetails,
                ...action.communityDetails,
            },
        },
    });
};

const updateCommunityUISettings = (state, action) => {
    return updateObject(state, {
        ...state,
        community: {
            ...state.community,
            communityDetails: {
                ...state.community.communityDetails,
                uiSettings: {
                    upgrades: {
                        ...state.community.communityDetails.uiSettings?.upgrades,
                        ...action.payload.uiSettings.upgrades,
                    },
                },
            },
        },
    });
};

const updateModelDuplicates = (state, action) => {
    const numDuplicates = Object.values(action.payload.copyDirections)
        .map((val) => (isNaN(val) ? 0 : val))
        .reduce((a, b) => a + b, 0);
    return updateObject(state, {
        ...state,
        community: {
            ...state.community,
            modelDir: {
                ...state.community.modelDir,
                models: {
                    ...state.community.modelDir.models,
                    [action.payload.modelId]: {
                        ...state.community.modelDir.models[action.payload.modelId],
                        countDirection: {
                            ...state.community.modelDir.models[action.payload.modelId].countDirection,
                            duplicates: numDuplicates,
                            copyDirections: action.payload.copyDirections,
                        },
                    },
                },
            },
        },
    });
};

// Updated backend snapshot after saving communtiy
const setInitData = (state, action) =>
    updateObject(state, {
        ...state,
        communityToCompare: {
            ...state.communityToCompare,
            communityDetails: cloneDeep(action.payload.communityDetails),
            modelDir: cloneDeep(action.payload.modelDir),
            modelData: cloneDeep(action.payload.modelData),
            // ecmInputMap: cloneDeep(action.payload.ecmInputMap),
        },
    });

export default communityReducer;
